import { useMemo } from 'react';
import ExplainerSection from '../components/ExplainerSection.js';
import Footer from '../components/Footer.js';
import IssuesCard from '../components/IssuesCard.js';
import LongTermIssuesCard from '../components/LongTermIssuesCard.js';
import MaintenancePage from '../components/MaintenancePage.js';
import ProviderGrid from '../components/ProviderGrid.js';
import RssCarousel from '../components/RssCarousel.js';
import SuspendedProvidersCard from '../components/SuspendedProvidersCard.js';
import TierFCard from '../components/TierFCard.js';
import TierStrip from '../components/TierStrip.js';
import { useDashboardState } from '../hooks/useDashboardState.js';
import { useLatencyFeed } from '../hooks/useLatencyFeed.js';
import { useRssFeed } from '../hooks/useRssFeed.js';
import type { ProviderListResponse, StateResponse } from '../lib/api.js';

// Home v0.7.1 — Phase 2D adds Tier 2 categorization (one ProviderGrid per
// category), per-provider description tooltips/modals, and the click-vs-icon
// split (provider names no longer navigate; the `↗` icon does). Polling
// cadence: /state every 60s (SSE primary), /bgp/recent every 5s during
// incidents, /latency/recent every 60s, /rss/items every 60s.

// Human-readable display names for the Tier 2 category slugs the API emits.
// Anything not in this map falls back to the slug itself, so a new category
// added server-side won't crash the UI — it just appears un-prettified.
const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  streaming: 'Streaming',
  gaming: 'Gaming',
  'social-media': 'Social Media',
  'ai-ml': 'AI / ML',
  cloud: 'Cloud Infrastructure',
  payments: 'Payments & Commerce',
  communications: 'Communications',
  productivity: 'Productivity & SaaS',
  observability: 'Observability & Support',
  'dev-tools': 'Dev Tools',
  healthcare: 'Healthcare & Medical',
  misc: 'Misc',
};

// Render order for Tier 2 category cards. Streaming/gaming/social land near
// the top because they're what most consumer visitors care about; misc is
// last so it never crowds the more-curated categories.
const CATEGORY_ORDER = [
  'streaming',
  'gaming',
  'social-media',
  'ai-ml',
  'cloud',
  'payments',
  'communications',
  'productivity',
  'observability',
  'dev-tools',
  'healthcare',
  'misc',
];

interface HomeContentProps {
  state: StateResponse;
  providers: ProviderListResponse;
}

export function HomeContent({ state, providers }: HomeContentProps) {
  const { latency: latencyResource } = useLatencyFeed();
  const { rss: rssResource } = useRssFeed();

  // Post-restructure: T1 providers now render inside category cards alongside
  // T2 (server-side ORDER BY tier sorts T1 first within each category). One
  // flat list of all providers feeds IssuesCard so it can surface any
  // degraded entry regardless of tier.
  const allProviders = useMemo(() => {
    if (!providers) return [];
    const fromCategories = Object.values(providers.categories ?? {}).flat();
    return [...providers.tier_0, ...fromCategories];
  }, [providers]);

  // Render categories in a stable, human-friendly order. Empty categories are
  // skipped. The right column scrolls naturally when there are many of them.
  const categoryCards = useMemo(() => {
    if (!providers) return [];
    const entries = providers.categories ?? {};
    return CATEGORY_ORDER.filter((slug) => (entries[slug]?.length ?? 0) > 0).map((slug) => ({
      slug,
      displayName: CATEGORY_DISPLAY_NAMES[slug] ?? slug,
      items: entries[slug] ?? [],
    }));
  }, [providers]);

  // Defensive: if `state.providers` is missing (e.g. an SSE event with the
  // wrong shape gets dispatched into the store), stay in the connecting state
  // rather than crashing children that index into it. The state↔stream
  // contract test in api-worker is the upstream guard; this is the
  // last-mile safety net.
  return (
    <div className="min-h-screen bg-bg text-text">
      {/* min-w-0 on each grid column is required so children with overflow-x
          actually overflow with a scrollbar instead of forcing the column to
          expand to fit all card content — without this the page can grow many
          viewports wide. */}
      <main className="grid lg:grid-cols-[2fr_1fr] gap-4 p-4 max-w-[1400px] mx-auto">
        <div className="space-y-4 min-w-0">
          <TierStrip
            providers={providers.tier_0}
            state={state.providers}
            latency={latencyResource}
          />
          <IssuesCard all={allProviders} state={state.providers} />
          {/* Below Active Issues: anything that's been red for >=24h moves
              here so prolonged degradations (e.g. AWS-region multi-day
              outages) don't crowd the day-to-day signal above. The card
              renders nothing when no long-term issues exist. */}
          <LongTermIssuesCard all={allProviders} state={state.providers} />
          {/* RSS carousel + explainer live in the left column so they share
              the same width as Tier 0 + Active Issues. The right column
              (Tier 1/2 categories) is naturally taller. */}
          <RssCarousel resource={rssResource} />
          <ExplainerSection />
          {/* Tier F (Untrustworthy) — providers excluded from the trusted
              grid. Renders nothing when the bucket is empty. Lives at the
              very bottom of the left column so visitors land on the actual
              status grid first; this card is reference, not an alert. */}
          <TierFCard providers={providers.tier_f ?? []} />
          <SuspendedProvidersCard providers={providers.suspended ?? []} />
        </div>
        <div className="space-y-4 min-w-0">
          {/* Categories merge T1 + T2 with a per-row T1/T2 badge. No dedicated
              TIER 1 block — T1 providers were tier-grouped before but they
              all live inside a category and most outages were dependency
              ripples from tier-0 anyway, so this surface reads more cleanly. */}
          {categoryCards.map(({ slug, displayName, items }) => (
            <ProviderGrid
              key={slug}
              title={displayName}
              providers={items}
              state={state.providers}
              storageKey={`panel:providers:${slug}`}
            />
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default function Home() {
  const { state, providers, stateResource } = useDashboardState();

  // Defensive: if `state.providers` is missing (e.g. an SSE event with the
  // wrong shape gets dispatched into the store), stay in the connecting state
  // rather than crashing children that index into it.
  if (!stateResource.ready || !state || !providers || !state.providers) {
    return <div className="p-6 font-mono text-dim">connecting…</div>;
  }

  if (state.maintenance?.enabled) {
    return <MaintenancePage maintenance={state.maintenance} />;
  }

  return <HomeContent state={state} providers={providers} />;
}
